/* ──────────────────────────────────────────────────────────────────────
 *  Chronicle Types — Type taxonomy with pillar grouping
 *
 *  Each chronicle type belongs to a pillar group and carries metadata
 *  used by the type-selection modal: icon, i18n keys for label and
 *  description, and a pillar colour token.
 *
 *  The `key` value is stored in the `categoryKey` column on the
 *  chronicle table.  Legacy keys (success_story, case_study, etc.)
 *  are mapped to the new taxonomy via LEGACY_CATEGORY_MAP.
 * ────────────────────────────────────────────────────────────────────── */

/* ── Pillar group definitions ──────────────────────────────────────── */

export interface ChroniclePillarGroup {
  id: string;
  labelKey: string;           // i18n key for group heading
  descriptionKey: string;     // i18n key for group description
  colorToken: string;         // CSS custom property for pillar accent
  types: ChronicleTypeDef[];
}

export interface ChronicleTypeDef {
  key: string;                // stored as categoryKey in DB
  icon: string;               // emoji icon for the card
  labelKey: string;           // i18n key for type name
  descriptionKey: string;     // i18n key for one-line description
  pillarGroup: string;        // back-reference to group id
}

/* ── Type definitions ─────────────────────────────────────────────── */

const PROJECT_WORK_TYPES: ChronicleTypeDef[] = [
  {
    key: 'project',
    icon: '🏗️',
    labelKey: 'chronicleTypes.project',
    descriptionKey: 'chronicleTypes.projectDesc',
    pillarGroup: 'project_work',
  },
  {
    key: 'initiative',
    icon: '🚀',
    labelKey: 'chronicleTypes.initiative',
    descriptionKey: 'chronicleTypes.initiativeDesc',
    pillarGroup: 'project_work',
  },
];

const IDEAS_EXPLORATION_TYPES: ChronicleTypeDef[] = [
  {
    key: 'innovation',
    icon: '💡',
    labelKey: 'chronicleTypes.innovation',
    descriptionKey: 'chronicleTypes.innovationDesc',
    pillarGroup: 'ideas_exploration',
  },
  {
    key: 'how_to',
    icon: '📖',
    labelKey: 'chronicleTypes.howTo',
    descriptionKey: 'chronicleTypes.howToDesc',
    pillarGroup: 'ideas_exploration',
  },
  {
    key: 'topic_tracker',
    icon: '🔭',
    labelKey: 'chronicleTypes.topicTracker',
    descriptionKey: 'chronicleTypes.topicTrackerDesc',
    pillarGroup: 'ideas_exploration',
  },
];

const LEARNING_GROWTH_TYPES: ChronicleTypeDef[] = [
  {
    key: 'learning',
    icon: '🎓',
    labelKey: 'chronicleTypes.learning',
    descriptionKey: 'chronicleTypes.learningDesc',
    pillarGroup: 'learning_growth',
  },
  {
    key: 'retrospective',
    icon: '🔍',
    labelKey: 'chronicleTypes.retrospective',
    descriptionKey: 'chronicleTypes.retrospectiveDesc',
    pillarGroup: 'learning_growth',
  },
];

const EXPERIENCE_TYPES: ChronicleTypeDef[] = [
  {
    key: 'travel',
    icon: '🌍',
    labelKey: 'chronicleTypes.travel',
    descriptionKey: 'chronicleTypes.travelDesc',
    pillarGroup: 'experience',
  },
  {
    key: 'event',
    icon: '🎤',
    labelKey: 'chronicleTypes.event',
    descriptionKey: 'chronicleTypes.eventDesc',
    pillarGroup: 'experience',
  },
];

/* ── Pillar groups (ordered for display) ──────────────────────────── */

export const CHRONICLE_PILLAR_GROUPS: ChroniclePillarGroup[] = [
  {
    id: 'project_work',
    labelKey: 'chronicleTypes.groupProjectWork',
    descriptionKey: 'chronicleTypes.groupProjectWorkDesc',
    colorToken: 'var(--pillar-projects)',
    types: PROJECT_WORK_TYPES,
  },
  {
    id: 'ideas_exploration',
    labelKey: 'chronicleTypes.groupIdeasExploration',
    descriptionKey: 'chronicleTypes.groupIdeasExplorationDesc',
    colorToken: 'var(--pillar-solutions)',
    types: IDEAS_EXPLORATION_TYPES,
  },
  {
    id: 'learning_growth',
    labelKey: 'chronicleTypes.groupLearningGrowth',
    descriptionKey: 'chronicleTypes.groupLearningGrowthDesc',
    colorToken: 'var(--pillar-enablers)',
    types: LEARNING_GROWTH_TYPES,
  },
  {
    id: 'experience',
    labelKey: 'chronicleTypes.groupExperience',
    descriptionKey: 'chronicleTypes.groupExperienceDesc',
    colorToken: 'var(--pillar-communities)',
    types: EXPERIENCE_TYPES,
  },
];

/* ── Flat lookup ──────────────────────────────────────────────────── */

export const ALL_CHRONICLE_TYPES: ChronicleTypeDef[] =
  CHRONICLE_PILLAR_GROUPS.flatMap((g) => g.types);

export const CHRONICLE_TYPE_MAP: Record<string, ChronicleTypeDef> =
  Object.fromEntries(ALL_CHRONICLE_TYPES.map((t) => [t.key, t]));

/* ── Legacy key mapping ───────────────────────────────────────────── */

/** Maps old categoryKey values to new type keys. */
export const LEGACY_CATEGORY_MAP: Record<string, string> = {
  success_story: 'project',
  case_study: 'project',
  reflection: 'retrospective',
  initiative: 'initiative',
};

/**
 * Resolve a categoryKey to its ChronicleTypeDef, handling legacy keys.
 * Returns undefined if the key is unrecognised.
 */
export function resolveChronicleType(categoryKey: string): ChronicleTypeDef | undefined {
  return CHRONICLE_TYPE_MAP[categoryKey]
    ?? CHRONICLE_TYPE_MAP[LEGACY_CATEGORY_MAP[categoryKey]];
}
